import { Injectable } from "@angular/core";
import { HttpRequestsService } from "./http-requests.service";
import {
  COUNTRY,
  ROUTE_PATHS,
  USER_ROUTES,
} from "../../app/constants/constants";
import { LocalStorageService } from "./local-storage.service";
import { LOCAL_STORAGE_KEYS } from "../global/constants";

export interface PaymentUser {
  qrCode: string;
  userType: string;
  receiverId: number;
  email: string;
  name: string;
  phone: string;
  phoneCountryCode: string;
  profileImage: string;
  profileImageLarge: string;
  profileImageThumbnail: string;
}

@Injectable({
  providedIn: "root",
})
export class UserService {
  constructor(
    private apiService: HttpRequestsService,
    private localstorage: LocalStorageService,
  ) {}

  editUserProfile = async (data) => {
    return await this.apiService.post(USER_ROUTES.PROFILE, data, true);
  };

  getCountryFlag = async () => {
    return await this.apiService.get(`${COUNTRY.COUNTRY_FLAG}`, true);
  };

  getCountry = async () => {
    return await this.apiService.get(`${COUNTRY.COUNTRY}`, true);
  };
  searchCountry = async (queryParams) => {
    return await this.apiService.get(
      `${COUNTRY.SEARCH_COUNTRY + "?" + queryParams}`,
      true,
    );
  };
  getRecentTransactions = async (data) => {
    return await this.apiService.post(
      USER_ROUTES.TRANSACTION_HISTORY,
      data,
      false,
    );
  };
  getCheckoutList = async () => {
    return await this.apiService.get(`${ROUTE_PATHS.CHECKOUTLIST}`, false);
  };
  addCheckout = async (data) => {
    return await this.apiService.post(ROUTE_PATHS.ADDEDITCHECKOUT, data, false);
  };
  editCheckout = async (data) => {
    return await this.apiService.put(ROUTE_PATHS.ADDEDITCHECKOUT, data, false);
  };
  updateExplorePin = async (data) => {
    return await this.apiService.post(
      ROUTE_PATHS.CHANGE_PIN_CHECKOUT,
      data,
      false,
    );
  };

  getCurrentCountryCode = async () => {
    const requestOptions: any = {
      method: "GET",
      redirect: "follow",
    };

    const response = await fetch(COUNTRY.CURRENT_COUNTRY_CODE, requestOptions);
    return await response.text();
  };

  createMerchant = async (body: any) => {
    const response: any = await this.apiService.post(
      `${USER_ROUTES.CREATE_MERCHANT}`,
      body,
      false,
      {},
      false,
    );
    if (response && response.data && response.data != null) {
      this.localstorage.setLocalStore("token", response.data.accessToken);
      this.localstorage.setLocalStore(
        "refreshtoken",
        response.data.refreshToken,
      );
      this.localstorage.setLocalStore(
        "businessname",
        response.data.businessName,
      );
      this.localstorage.setLocalStore("profile", response.data.logo);
      this.localstorage.setLocalStore(
        "userDetails",
        JSON.stringify(response.data),
      );
      this.localstorage.setDataInIndexedDB(
        LOCAL_STORAGE_KEYS.MOBILECOUNTRYCODE,
        response.data.phoneCountryCode,
      );
      this.localstorage.setDataInIndexedDB(
        LOCAL_STORAGE_KEYS.CURRENCY_SYMBOL,
        response.data.currencySymbol,
      );
      await this.localstorage.setDataInIndexedDB(
        LOCAL_STORAGE_KEYS.ID,
        response.data.id,
      );
      await this.localstorage.setDataInIndexedDB(
        LOCAL_STORAGE_KEYS.FIRSTNAME,
        response.data.firstName,
      );
      await this.localstorage.setDataInIndexedDB(
        LOCAL_STORAGE_KEYS.LASTNAME,
        response.data.lastName,
      );
    }
    return response;
  };

  verifyEmail = async (body: { email: string; otp: string }) => {
    return await this.apiService.post(USER_ROUTES.VERIFY_MERCHANT_EMAIL, body);
  };

  resendEmailVeririficationOTP = async (body: { email: string }) => {
    return await this.apiService.post(USER_ROUTES.RESEND_EMAIL_OTP, body);
  };

  determineRegistrationComplete = (user: any): boolean => {
    if (
      !user.emailVerify ||
      !user.firstName ||
      !user.lastName ||
      !user.country ||
      !user.isPinSet ||
      !user.businessName ||
      !user.businessCountry
    ) {
      return false;
    }
    return true;
  };

  setPersonalDetails = async (data) => {
    return await this.apiService.post(
      USER_ROUTES.SAVE_MERCHANT_PERSONAL_DETAILS,
      data,
    );
  };

  setBusinessLocation = async (data) => {
    return await this.apiService.put(USER_ROUTES.SAVE_BUSINESS_LOCATION, data);
  };

  setBusinessDetails = async (data) => {
    return await this.apiService.put(USER_ROUTES.SAVE_BUSINESS_DETAILS, data);
  };

  getSupportedRegions = async (id: number = 0) => {
    return await this.apiService.post(
      `${USER_ROUTES.GET_SUPPORTED_COUNTRIES}`,
      { id: id },
    );
  };

  setSmartPayPin(body: any) {
    return this.apiService.post(USER_ROUTES.CREATE_PIN, body);
  }

  getBusinessCategories = async () => {
    return await this.apiService.get(
      USER_ROUTES.BUSINESS_CATEGORIES_LIST,
      false,
    );
  };

  getBusinessTypes = async () => {
    return await this.apiService.get(USER_ROUTES.BUSINESS_TYPES_LIST, false);
  };

  getSourceOfIncome = async () => {
    return await this.apiService.get(
      `${USER_ROUTES.SOURCE_OF_INCOME_LIST}/MERCHANT`,
      false,
    );
  };

  getUserDetailsByQrCodes = async (
    qrCodes: string[],
  ): Promise<PaymentUser[]> => {
    const dto = await this.apiService.post<{ data: { users: any[] } }>(
      USER_ROUTES.GET_PAYMENT_USER_DETAIL_ON_QR_CODES,
      {
        qrCodes,
      },
    );
    return (dto?.data?.users || []).map((user) => ({
      ...user,
    }));
  };
}
